import { useQuery } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export interface CostCodeData {
  costCodeId: string;
  code: string;
  name: string;
  division: string;
  budget: number;
  committed: number;
  actuals: number;
  remaining: number;
  variance: number;
  percentCommitted: number;
  status: "under" | "at" | "over";
}

export interface DivisionData {
  division: string;
  budget: number;
  committed: number;
  actuals: number;
  variance: number;
  status: "under" | "at" | "over";
}

export interface RecentCommitment {
  id: string;
  vendorName: string;
  type: "po" | "subcontract";
  amount: number;
  costCode: string;
  date: string;
  status: string;
}

export interface CostDashboardSummary {
  totalBudget: number;
  totalCommitted: number;
  totalActuals: number;
  totalRemaining: number;
  totalVariance: number;
  percentCommitted: number;
  percentSpent: number;
  atRiskAmount: number;
  healthStatus: "healthy" | "warning" | "critical";
  costCodeCount: number;
  overBudgetCount: number;
  atBudgetCount: number;
}

export interface CostDashboardResponse {
  projectId: string;
  summary: CostDashboardSummary;
  byDivision: DivisionData[];
  byCostCode: CostCodeData[];
  recentCommitments: RecentCommitment[];
}

/**
 * Hook to fetch cost dashboard data for a project
 */
export function useCostDashboard(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "cost-dashboard"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CostDashboardResponse>(`/api/projects/${projectId}/cost-dashboard`, {
        headers,
      });
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes - matches other financial data hooks
  });
}
